import { useCallback, useEffect, useRef, useState } from "react";
import {
  CameraKitProvider,
  createConsoleLogger,
  LensPlayer,
  LiveCanvas,
  useCameraKit,
} from "@snap/react-camera-kit";
import {
  SNAP_FILTERS_VISIBLE,
  SNAP_FRONT_CAMERA_SOURCE,
  hasActiveSnapLens,
} from "../config/snapFilters";
import {
  getSavedCameraFilter,
  saveCameraFilter,
} from "../../camera/cameraFilterSettings";
import SnapFilterBar from "./SnapFilterBar";

function stopPreviewVideoElements(root) {
  if (!root) return;

  const videos = root.querySelectorAll("video");
  videos.forEach((video) => {
    const stream = video.srcObject;
    if (stream && typeof stream.getTracks === "function") {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          // ignore
        }
      });
    }
    video.srcObject = null;
  });
}

function readViewportSize() {
  const visualViewport = window.visualViewport;
  return {
    width: Math.round(visualViewport?.width ?? window.innerWidth),
    height: Math.round(visualViewport?.height ?? window.innerHeight),
  };
}

// The earlier no_filter breakage was observed while the Capacitor WebView
// origin mismatch (https://localhost vs the Snap Portal's allowed platform
// identifier) was still unresolved. Now that the origin has been fixed to
// https://livora-3kse.web.app, start with the real no_filter / plain camera
// again for the fastest possible open. A one-time automatic fallback below
// guards against no_filter still failing to become ready for some other
// reason (see NO_FILTER_READY_TIMEOUT_MS).
const NO_FILTER_ID = "no_filter";

// Known-working lens used only as a safety-net fallback if no_filter's
// camera/player never reaches "ready", or the SDK/source errors out.
// Never the previously-selected filter, and never the heavy cartoon_camera
// lens (still intentionally excluded from auto-load).
const FALLBACK_FILTER_ID = "luminous_femme";
const NO_FILTER_READY_TIMEOUT_MS = 7000;

function getFilterById(id) {
  return SNAP_FILTERS_VISIBLE.find((filter) => filter.id === id);
}

function getInitialFilter() {
  // Start with no filter / plain camera. Do NOT auto-load the previously
  // saved filter and do NOT auto-load cartoon_camera here.
  return getFilterById(NO_FILTER_ID) ?? SNAP_FILTERS_VISIBLE[0];
}

/**
 * @param {{ sdkStatus: string, sourceStatus: string, lensStatus: string, filterEnabled: boolean, sourceError?: Error, lensError?: Error, loadingLabel: string, sdkErrorLabel: string, cameraErrorLabel: string, lensErrorLabel: string }} props
 */
function SnapCameraStatusOverlay({
  sdkStatus,
  sourceStatus,
  lensStatus,
  filterEnabled,
  sourceError,
  lensError,
  loadingLabel,
  sdkErrorLabel,
  cameraErrorLabel,
  lensErrorLabel,
}) {
  if (sdkStatus === "error") {
    return (
      <div className="snap-camera-status snap-camera-status--error">
        <p>{sdkErrorLabel}</p>
      </div>
    );
  }

  if (sdkStatus !== "ready" || sourceStatus === "loading") {
    return (
      <div className="snap-camera-status">
        <div className="snap-camera-status__spinner" />
        <p>{loadingLabel}</p>
      </div>
    );
  }

  if (sourceStatus === "error") {
    return (
      <div className="snap-camera-status snap-camera-status--error">
        <p>{sourceError?.message ?? cameraErrorLabel}</p>
      </div>
    );
  }

  if (filterEnabled && lensStatus === "error") {
    return (
      <div className="snap-camera-status snap-camera-status--error">
        <p>{lensError?.message ?? lensErrorLabel}</p>
      </div>
    );
  }

  return null;
}

function SnapCameraExperience() {
  const { sdkStatus, source, lens, fetchLens } = useCameraKit();
  const [selectedFilter, setSelectedFilter] = useState(getInitialFilter);
  const [viewportSize, setViewportSize] = useState(readViewportSize);
  const cameraScreenRef = useRef(null);
  const pendingSelectLogRef = useRef(null);
  const cameraReadyLoggedRef = useRef(false);
  const noFilterFallbackAppliedRef = useRef(false);
  const preloadStartedRef = useRef(false);

  const lensActive = hasActiveSnapLens(selectedFilter);
  const isLensLoading = lens.status === "loading";

  useEffect(() => {
    console.log("[SNAP_PERF] first_render");
  }, []);

  useEffect(() => {
    if (cameraReadyLoggedRef.current) return;
    if (sdkStatus === "ready" && source.status === "ready") {
      console.log("[SNAP_PERF] camera_or_player_ready");
      cameraReadyLoggedRef.current = true;
    }
  }, [sdkStatus, source.status]);

  // Safety net: if starting with no_filter never gets the camera/player to
  // "ready" (or the SDK/source errors), fall back once to a known-working
  // lens. Only relevant while no_filter is still the active selection - once
  // the user picks something else, or the fallback already fired, this is a
  // no-op.
  useEffect(() => {
    if (noFilterFallbackAppliedRef.current) return;
    if (selectedFilter.id !== NO_FILTER_ID) return;

    if (sdkStatus === "error" || source.status === "error") {
      const fallbackFilter = getFilterById(FALLBACK_FILTER_ID);
      if (!fallbackFilter) return;
      noFilterFallbackAppliedRef.current = true;
      console.warn(
        `[SNAP_PERF] no_filter_fallback reason=${sdkStatus === "error" ? "sdk_error" : "source_error"} filterId=${FALLBACK_FILTER_ID}`
      );
      setSelectedFilter(fallbackFilter);
      return undefined;
    }

    if (source.status === "ready") return undefined;

    const timer = setTimeout(() => {
      if (noFilterFallbackAppliedRef.current || source.status === "ready") return;
      const fallbackFilter = getFilterById(FALLBACK_FILTER_ID);
      if (!fallbackFilter) return;
      noFilterFallbackAppliedRef.current = true;
      console.warn(
        `[SNAP_PERF] no_filter_fallback reason=timeout filterId=${FALLBACK_FILTER_ID}`
      );
      setSelectedFilter(fallbackFilter);
    }, NO_FILTER_READY_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [selectedFilter.id, sdkStatus, source.status]);

  // Background preload: once the camera/player is ready, warm up the visible
  // lenses' metadata via Camera Kit's public fetchLens API so a later tap on
  // a filter is faster. This never calls applyLens and never blocks the
  // initial camera render - it only runs after camera_or_player_ready.
  useEffect(() => {
    if (preloadStartedRef.current) return;
    if (sdkStatus !== "ready" || source.status !== "ready") return;

    const lensesToPreload = SNAP_FILTERS_VISIBLE.filter(hasActiveSnapLens);
    if (lensesToPreload.length === 0) return;

    preloadStartedRef.current = true;
    let cancelled = false;

    console.log("[SNAP_PRELOAD] start");

    (async () => {
      for (const filter of lensesToPreload) {
        if (cancelled) break;
        try {
          await fetchLens(filter.lensId, filter.lensGroupId);
          if (!cancelled) {
            console.log(`[SNAP_PRELOAD] lens_cached filterId=${filter.id}`);
          }
        } catch (error) {
          if (!cancelled) {
            console.warn(`[SNAP_PRELOAD] error filterId=${filter.id}`, error);
          }
        }
      }
      if (!cancelled) {
        console.log("[SNAP_PRELOAD] done");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sdkStatus, source.status, fetchLens]);

  useEffect(() => {
    const updateViewportSize = () => {
      setViewportSize(readViewportSize());
    };

    window.addEventListener("resize", updateViewportSize);
    window.addEventListener("orientationchange", updateViewportSize);
    window.visualViewport?.addEventListener("resize", updateViewportSize);
    window.visualViewport?.addEventListener("scroll", updateViewportSize);

    return () => {
      window.removeEventListener("resize", updateViewportSize);
      window.removeEventListener("orientationchange", updateViewportSize);
      window.visualViewport?.removeEventListener("resize", updateViewportSize);
      window.visualViewport?.removeEventListener("scroll", updateViewportSize);
    };
  }, []);

  const handleSelectFilter = useCallback(
    (filter) => {
      if (lens.status === "loading") return;

      setSelectedFilter((current) => {
        if (filter.id === current.id) return current;

        if (process.env.NODE_ENV === "development") {
          console.log("[SNAP_FILTER_SELECTED]", filter);
        }

        console.log(`[SNAP_PERF] filter_select_start filterId=${filter.id}`);
        pendingSelectLogRef.current = filter.id;

        saveCameraFilter({
          id: filter.id === "no_filter" ? "none" : filter.id,
          provider: "snap",
          intensity: getSavedCameraFilter().intensity,
        });
        return filter;
      });
    },
    [lens.status]
  );

  useEffect(() => {
    if (!pendingSelectLogRef.current || pendingSelectLogRef.current !== selectedFilter.id) {
      return;
    }

    if (!lensActive || lens.status === "ready") {
      console.log(`[SNAP_PERF] filter_select_done filterId=${selectedFilter.id}`);
      pendingSelectLogRef.current = null;
    }
  }, [lensActive, lens.status, selectedFilter]);

  useEffect(() => {
    if (source.error) {
      console.error("[SNAP_LENS_ERROR]", source.error);
      console.error("[SNAP_CAMERA_ERROR]", source.error);
    }
  }, [source.error]);

  useEffect(() => {
    if (lens.error) {
      console.error("[SNAP_LENS_ERROR]", lens.error);
    }
  }, [lens.error]);

  useEffect(() => {
    if (lensActive && lens.status === "error" && lens.error) {
      console.error("[SNAP_LENS_ERROR]", {
        filterId: selectedFilter.id,
        lensId: selectedFilter.lensId,
        lensGroupId: selectedFilter.lensGroupId,
        message: lens.error?.message,
        error: lens.error,
      });
      console.error(`[SNAP_PERF] lens_error filterId=${selectedFilter.id}`);
      pendingSelectLogRef.current = null;
    }
  }, [lensActive, lens.status, lens.error, selectedFilter]);

  useEffect(() => {
    const screen = cameraScreenRef.current;
    return () => {
      stopPreviewVideoElements(screen);
    };
  }, []);

  const activeLensId = lensActive ? selectedFilter.lensId : undefined;
  const activeLensGroupId = lensActive ? selectedFilter.lensGroupId : undefined;

  return (
    <div ref={cameraScreenRef} className="snap-camera-screen">
      <LensPlayer
        className="snap-camera-player"
        lensId={activeLensId}
        lensGroupId={activeLensGroupId}
        source={SNAP_FRONT_CAMERA_SOURCE}
        outputSize={{
          mode: "fixed",
          width: viewportSize.width,
          height: viewportSize.height,
        }}
      >
        <div className="snap-camera-viewport">
          <LiveCanvas className="snap-camera-live-canvas" />
        </div>
      </LensPlayer>

      <SnapCameraStatusOverlay
        sdkStatus={sdkStatus}
        sourceStatus={source.status}
        lensStatus={lens.status}
        filterEnabled={lensActive}
        sourceError={source.error}
        lensError={lens.error}
        loadingLabel="Opening camera..."
        sdkErrorLabel="Failed to load Camera Kit"
        cameraErrorLabel="Could not access camera"
        lensErrorLabel="Could not load filter"
      />

      <SnapFilterBar
        selectedFilterId={selectedFilter.id}
        onSelect={handleSelectFilter}
        isLensLoading={isLensLoading}
      />
    </div>
  );
}

/**
 * @param {{ apiToken: string }} props
 */
export default function SnapCameraView({ apiToken }) {
  return (
    <CameraKitProvider
      apiToken={apiToken}
      logger={createConsoleLogger()}
      logLevel="warn"
    >
      <SnapCameraExperience />
    </CameraKitProvider>
  );
}
